import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';

// Component imports
import Modal from './components/Modal';
import NavBar from './components/NavBar';
import AuthForm from './components/AuthForm';

// Page imports
import Home from './pages/Home';
import MoodSearch from './pages/MoodSearch';
import Contact from './pages/Contact';
import About from './pages/About';
import MoodHistory from './components/MoodHistory';

/**
 * Main App Component
 * 
 * Handles:
 * - Authentication state management
 * - Routing between pages
 * - User session management
 * - Guest mode functionality
 */
export default function App() {
  // Authentication state
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [skipLogin, setSkipLogin] = useState(localStorage.getItem('skipLogin') === 'true');
  const [username, setUsername] = useState(null);
  
  // UI state
  const [modalMode, setModalMode] = useState(null);
  const [navbarModalMode, setNavbarModalMode] = useState(null);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  
  // Persistent search state - survives page navigation
  const [searchState, setSearchState] = useState({
    tracks: [],
    loading: false,
    error: null,
    showCreateButton: false,
    suggestedMood: '',
    deletedTrackIds: new Set(),
    showGetMoreSongs: false,
    trackIdCounter: 0,
    loadingMoreSongs: false,
    companionShownEarly: false,
    moodId: null, // Store moodId for database operations
  });

  /**
   * Decode JWT token and extract username on component mount
   * Runs whenever token changes
   */
  useEffect(() => {
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUsername(decoded.username);
      } catch (error) {
        console.error("Failed to decode token:", error);
        setUsername(null);
        // Clear invalid token
        handleLogout();
      }
    }
  }, [token]);

  /**
   * Handle scroll event to show/hide scroll-to-top button
   */
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setShowScrollToTop(scrollTop > 300); // Show button after scrolling 300px
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  /**
   * Scroll to top of page
   */
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  /**
   * Handle successful authentication
   * @param {string} token - JWT token from successful login/signup
   */
  const handleAuthSuccess = (token) => {
    localStorage.setItem('token', token);
    setToken(token);
    
    // Disable guest mode when user authenticates
    localStorage.setItem('skipLogin', 'false');
    setSkipLogin(false);
  
    // Extract and store username from token
    const decoded = jwtDecode(token);
    setUsername(decoded.username);
  
    // Close auth modals
    setModalMode(null);
    setNavbarModalMode(null);
  };

  /**
   * Enable guest mode - allows users to use app without account
   */
  const handleSkipLogin = () => {
    localStorage.setItem('skipLogin', 'true');
    setSkipLogin(true);
  };

  /**
   * Handle user logout
   * Clears all authentication data and resets state
   */
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('skipLogin');
    setToken(null);
    setSkipLogin(false);
    setUsername(null);
  };

  /**
   * Handle navbar login button click
   * Shows login modal instead of navigating
   */
  const handleNavbarLogin = () => {
    setNavbarModalMode('login');
  };

  /**
   * Handle navbar logout button click
   * Shows confirmation modal
   */
  const handleNavbarLogout = () => {
    setShowLogoutConfirm(true);
  };

  /**
   * Handle confirmed logout
   * Switches to guest mode instead of full logout
   */
  const handleConfirmLogout = () => {
    // Clear token but maintain guest mode
    localStorage.removeItem('token');
    setToken(null);
    setUsername(null);
    
    // Ensure guest mode is enabled
    localStorage.setItem('skipLogin', 'true');
    setSkipLogin(true);
    
    // Close confirmation modal
    setShowLogoutConfirm(false);
  };

  /**
   * Handle cancelled logout
   */
  const handleCancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  // Computed authentication states
  const isAuthenticated = token || skipLogin;
  const isGuest = skipLogin && !token;
  const isAnonymous = !token && !skipLogin; // New anonymous user

  // Landing page styles
  const styles = {
    wrapper: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #6bbcff, #a979ff)',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '2rem 1rem',
      fontFamily: '"Segoe UI", sans-serif',
      textAlign: 'center',
      color: 'white',
    },
    title: {
      fontSize: '2.5rem',
      fontWeight: 700,
      marginBottom: '1rem',
    },
    subtitle: {
      fontSize: '1.2rem',
      maxWidth: '600px',
      marginBottom: '2rem',
      lineHeight: '1.6',
    },
    skipButton: {
      backgroundColor: 'transparent',
      color: '#fff',
      border: '2px solid white',
      borderRadius: '8px',
      padding: '0.75rem 1.5rem',
      fontSize: '1rem',
      marginBottom: '1.5rem',
      cursor: 'pointer',
      fontWeight: 600,
      transition: 'all 0.3s ease',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
      },
    },
    authButtons: {
      display: 'flex',
      gap: '1rem',
      justifyContent: 'center',
      flexWrap: 'wrap',
    },
    primaryBtn: {
      backgroundColor: '#ffffff',
      color: '#5c33ff',
      padding: '0.75rem 1.5rem',
      border: 'none',
      borderRadius: '8px',
      fontSize: '1rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: '0.3s ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      },
    },
    secondaryBtn: {
      backgroundColor: '#f4f4f4',
      color: '#5c33ff',
      padding: '0.75rem 1.5rem',
      border: 'none',
      borderRadius: '8px',
      fontSize: '1rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: '0.3s ease',
      '&:hover': {
        backgroundColor: '#e5e5e5',
        transform: 'translateY(-2px)',
      },
    },
    // Logout Confirmation Modal Styles
    logoutConfirm: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '1.5rem',
      padding: '2rem 3rem',
      fontFamily: '"Segoe UI", sans-serif',
      maxWidth: '1200px',
      width: '100%',
      backgroundColor: 'white',
      borderRadius: '12px',
      boxSizing: 'border-box',
    },
    logoutIcon: {
      color: '#f59e0b',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    logoutTitle: {
      fontSize: '1.2rem',
      fontWeight: 600,
      color: '#1f2937',
      margin: '0',
      textAlign: 'center',
      lineHeight: '1.3',
    },
    logoutButtons: {
      display: 'flex',
      gap: '1rem',
      justifyContent: 'center',
    },
    cancelButton: {
      backgroundColor: '#f3f4f6',
      color: '#374151',
      padding: '0.75rem 1.5rem',
      border: 'none',
      borderRadius: '8px',
      fontSize: '1rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      minWidth: '100px',
    },
    confirmButton: {
      backgroundColor: '#dc2626',
      color: 'white',
      padding: '0.75rem 1.5rem',
      border: 'none',
      borderRadius: '8px',
      fontSize: '1rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      minWidth: '100px',
    },
    scrollToTopButton: {
      position: 'fixed',
      bottom: '2rem',
      right: '2rem',
      width: '50px',
      height: '50px',
      borderRadius: '50%',
      backgroundColor: '#5c33ff',
      color: 'white',
      border: 'none',
      fontSize: '1.5rem',
      fontWeight: 'bold',
      cursor: 'pointer',
      boxShadow: '0 4px 12px rgba(92, 51, 255, 0.3)',
      transition: 'all 0.3s ease',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '&:hover': {
        backgroundColor: '#4c2ddb',
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 16px rgba(92, 51, 255, 0.4)',
      },
    },
  };

  return (
    <>
      <style>
        {`
          /* Scroll to Top Button Responsive Styles */
          @media (max-width: 768px) {
            .scroll-to-top-button {
              width: 45px !important;
              height: 45px !important;
              bottom: 1.5rem !important;
              right: 1.5rem !important;
              font-size: 1.3rem !important;
            }
          }
          
          @media (max-width: 480px) {
            .scroll-to-top-button {
              width: 40px !important;
              height: 40px !important;
              bottom: 1rem !important;
              right: 1rem !important;
              font-size: 1.2rem !important;
            }
          }
          
          /* Logout Modal Responsive Styles - Target modals with 500px width */
          @media (max-width: 768px) {
            .modal[style*="width: 500px"] {
              width: 90vw !important;
              max-width: 400px !important;
              padding: 1.5rem 2rem !important;
            }
            .modal[style*="width: 500px"] h3 {
              font-size: 1.1rem !important;
            }
            .modal[style*="width: 500px"] div[style*="display: flex"][style*="gap: 1rem"] {
              gap: 0.8rem !important;
            }
            .modal[style*="width: 500px"] button:not(.modal-close) {
              padding: 0.7rem 1.2rem !important;
              font-size: 0.9rem !important;
              min-width: 80px !important;
            }
          }
          
          @media (max-width: 480px) {
            .modal[style*="width: 500px"] {
              width: 95vw !important;
              max-width: 350px !important;
              padding: 1.2rem 1.5rem !important;
            }
            .modal[style*="width: 500px"] h3 {
              font-size: 1rem !important;
            }
            .modal[style*="width: 500px"] div[style*="display: flex"][style*="gap: 1rem"] {
              gap: 0.6rem !important;
              flex-direction: column !important;
              width: 100% !important;
            }
            .modal[style*="width: 500px"] button:not(.modal-close) {
              padding: 0.8rem 1rem !important;
              font-size: 0.85rem !important;
              min-width: 100% !important;
              width: 100% !important;
            }
          }
        `}
      </style>
    <Router>
      {/* Navigation Bar - Always show */}
      <NavBar 
        isAuthenticated={isAuthenticated}
        isGuest={isGuest}
        isAnonymous={isAnonymous}
        onLogout={handleNavbarLogout}
        onLogin={handleNavbarLogin}
        username={username}
      />
      
      <div>
        {/* Main App Routes - Always show the Home page */}
        <Routes>
          <Route path="/" element={<Home username={username} isGuest={isGuest || isAnonymous} />} />
          <Route path="/search" element={<MoodSearch token={token} searchState={searchState} setSearchState={setSearchState} />} />
          <Route 
            path="/history" 
            element={<MoodHistory isGuest={isGuest || isAnonymous} />} 
          />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          
          {/* Catch-all route - redirect to home */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>

        {/* Authentication Modal */}
        <Modal isOpen={!!modalMode} onClose={() => setModalMode(null)}>
          <AuthForm 
            mode={modalMode} 
            onAuthSuccess={handleAuthSuccess}
            onModeChange={setModalMode}
          />
        </Modal>

        {/* Navbar Authentication Modal */}
        <Modal isOpen={!!navbarModalMode} onClose={() => setNavbarModalMode(null)}>
          <AuthForm 
            mode={navbarModalMode} 
            onAuthSuccess={handleAuthSuccess}
            onClose={() => setNavbarModalMode(null)}
            onModeChange={setNavbarModalMode}
          />
        </Modal>

        {/* Logout Confirmation Modal */}
        <Modal 
          isOpen={showLogoutConfirm} 
          onClose={handleCancelLogout}
          customStyle={{ width: '500px', maxWidth: '90vw' }}
        >
          <div style={styles.logoutConfirm}>
                <h3 style={styles.logoutTitle}>Are you sure you want to log out?</h3>
                <div style={styles.logoutButtons}>
                  <button 
                    onClick={handleCancelLogout} 
                    style={styles.cancelButton}
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={handleConfirmLogout} 
                    style={styles.confirmButton}
                  >
                    Log Out
                  </button>
                </div>
              </div>
            </Modal>

        {/* Scroll to Top Button */}
        {showScrollToTop && (
          <button
            onClick={scrollToTop}
            style={styles.scrollToTopButton}
            className="scroll-to-top-button"
            title="Go to top"
          >
            ↑
          </button>
        )}
      </div>
    </Router>
    </>
  );
}
